import { useCallback, useEffect, useState } from 'react';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';

type UseStateDebounceChangeReturn<T> = [
  value: T | undefined,
  setValue: (v: T) => void,
];

/**
 * Works like useState, state is always current, but the external onChange callback is debounced.
 * This is useful if the external onChange is expensive to perform and causes poor INP
 * or similar issues.
 *
 * It can still be externally controlled if a parent changes initialValue.
 */
const useStateDebounceChange = <T>(
  debouncedCallback: (...args: any) => any, initialValue?: T,
): UseStateDebounceChangeReturn<T> => {
  const [state, setState] = useState<T | undefined>(initialValue);
  const update = useAfterRenderCallback((v) => debouncedCallback(v));

  const debounceState = useCallback((v: T) => {
    setState(v);
    update(v);
  }, [update]);

  useEffect(() => setState(initialValue), [initialValue]);
  return [state, debounceState];
};

export default useStateDebounceChange;
